// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import HeatmapPanelTemplate from '@front/templates/HeatmapPanelTemplate.html';
import { HeatmapSymbolTitleBy, HeatmapSymbolFirstValue, HeatmapSymbolSecondValue, HeatmapSymbolSizeBy, HeatmapSymbolColorBy, getHeatmapSymbolTitleByKey, getHeatmapSymbolFirstValueKey, getHeatmapSymbolSecondValueKey, getHeatmapSymbolSizeByKey, getHeatmapSymbolColorByKey } from '@shared/commons/Heatmap/Enums';
import { HeatmapData } from '@shared/commons/Heatmap/Models/HeatmapData';
import { DataCache } from '@shared/commons/DataCache';
import { CustomInstrumentListTypeEnum } from '@shared/utils/CustomInstrumentList/CustomInstrumentListTypeEnum';
import { PanelSettingsScreen } from '../screen/PanelSettingsScreen';
import { Resources } from '@shared/localizations/Resources';
import { DynProperty, DynPropertyRelationType, TrioColor } from '@shared/commons/DynProperty';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { LinkedSystem } from '../misc/LinkedSystem';
import { CustomerAccess } from '@shared/commons/CustomerAccess/CustomerAccess';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { ApplicationPanel } from './ApplicationPanel';
import { WatchlistManager } from '@shared/utils/Managers/WatchlistManager/WatchlistManager';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { createContextMenuItemsFromEnum, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { PropertySetupScreen } from '../screen/PropertySetupScreen';
import { AdvancedOrderEntry } from './AdvancedOrderEntry';
import { SymbolInfoPanel } from './SymbolInfoPanel';
export class HeatmapPanel extends ApplicationPanel {
    constructor() {
        super();
        this._selectedListId = undefined;
        this._marketCapCache = new Map();
        this.onUpdateItems = () => {
            const selectedListItem = this.get('selectedListItem');
            if (isNullOrUndefined(selectedListItem)) {
                return;
            }
            const data = this._heatmapDrawer.getData();
            for (let i = 0; i < data.length; i++) {
                const item = data[i];
                const algorithmData = DataCache.HeatmapCache.getData(selectedListItem.id, item.Instrument);
                item.heatmapAlgorithmData = algorithmData;
            }
            this._heatmapDrawer.updateMap();
        };
        this.onDisplayModeChanged = () => this._heatmapDrawer.updateMap();
        this.onHoveredSymbolChanged = (hoveredHeatmapData) => {
            if (isNullOrUndefined(hoveredHeatmapData)) {
                void this.set('hoveredHeatmapData', null);
            }
            else {
                void this.set('hoveredHeatmapData', hoveredHeatmapData);
            }
        };
        this.onSymbolClicked = (instrument) => {
            this.symbolLink_Out(false, instrument);
        };
        this.Name = 'HeatmapPanel';
        this.headerLocaleKey = 'panel.HeatmapPanel';
    }
    getType() {
        return PanelNames.HeatmapPanel;
    }
    oninit() {
        super.oninit();
        super.on('onListItemClicked', this.listItemClicked);
        super.on('scaleFilterChanged', this.onScaleFilterChanged);
        super.on('scaleMultiplierChanged', this.onScaleMultiplierChanged);
        super.on('onMoreButtonClicked', this.onMoreButtonClick);
        super.observe('selectedListItem', this.selectedListItemChanged);
    }
    onteardown() {
        DataCache.OnUpdateInstrument.UnSubscribe(this.onUpdateInstrument, this);
        BaseSettings.displayModeChanged.UnSubscribe(this.onDisplayModeChanged, this);
        this._heatmapDrawer.unsubscribeOnHoveredSymbolChanged(this.onHoveredSymbolChanged);
        this._heatmapDrawer.unsubscribeOnSymbolClicked(this.onSymbolClicked);
    }
    oncomplete() {
        super.oncomplete();
        const heatmapControl = this.findComponent('heatmapRactive');
        this._heatmapDrawer = heatmapControl.getDrawer();
        this._heatmapDrawer.subscribeOnHoveredSymbolChanged(this.onHoveredSymbolChanged);
        this._heatmapDrawer.subscribeOnSymbolClicked(this.onSymbolClicked);
        this.deserializeDelayedData();
        DataCache.OnUpdateInstrument.Subscribe(this.onUpdateInstrument, this);
        BaseSettings.displayModeChanged.Subscribe(this.onDisplayModeChanged, this);
        this.repopulate();
    }
    repopulate() {
        this.populateListItems();
        this.populateIntervals();
    }
    localize() {
        super.localize();
        const heatmapSymbolInfo = super.findComponent('heatmapSymbolInfo');
        if (!isNullOrUndefined(heatmapSymbolInfo)) {
            heatmapSymbolInfo.localize();
        }
    }
    themeChange() {
        super.themeChange();
        this._heatmapDrawer.ThemeChanged();
        this.populateIntervals();
    }
    onContextMenuOpen(event) {
        super.onContextMenuOpen(event);
        this.showContextMenu(event);
    }
    Properties() {
        const props = super.Properties();
        const viewSettings = this._heatmapDrawer.getSettings().viewSettings;
        let dp;
        let separatorGroup = `#0#${Resources.getResource('panel.HeatmapPanel.Settings.SectorLevel')}`;
        dp = new DynProperty('sectorBackColor', viewSettings.sectorLevelBackgroundColor, DynProperty.COLOR, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.Color';
        dp.separatorGroup = separatorGroup;
        props.push(dp);
        dp = new DynProperty('sectorForeColor', viewSettings.sectorLevelFontColor, DynProperty.COLOR, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.FontColor';
        dp.separatorGroup = separatorGroup;
        props.push(dp);
        separatorGroup = `#1#${Resources.getResource('panel.HeatmapPanel.Settings.IndustryLevel')}`;
        dp = new DynProperty('industryBackColor', viewSettings.industryLevelBackgroundColor, DynProperty.COLOR, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.Color';
        dp.separatorGroup = separatorGroup;
        props.push(dp);
        dp = new DynProperty('industryForeColor', viewSettings.industryLevelFontColor, DynProperty.COLOR, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.FontColor';
        dp.separatorGroup = separatorGroup;
        props.push(dp);
        separatorGroup = `#2#${Resources.getResource('panel.HeatmapPanel.Settings.SymbolLevel')}`;
        dp = new DynProperty('symbolScaleColor', new TrioColor(viewSettings.minColor, viewSettings.midColor, viewSettings.maxColor), DynProperty.TRIO_COLOR, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.ScaleColors';
        dp.separatorGroup = separatorGroup;
        dp.assignedProperty = ['symbolScaleExample'];
        dp.DynPropertyRelationType = DynPropertyRelationType.Value;
        props.push(dp);
        dp = new DynProperty('symbolScaleExample', new TrioColor(viewSettings.minColor, viewSettings.midColor, viewSettings.maxColor), DynProperty.TRIO_COLOR_GRADIENT, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.Example';
        dp.separatorGroup = separatorGroup;
        props.push(dp);
        dp = new DynProperty('symbolTitle', viewSettings.symbolLevelTitleBy, DynProperty.COMBOBOX, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.Title';
        dp.separatorGroup = separatorGroup;
        dp.objectVariants = createContextMenuItemsFromEnum(HeatmapSymbolTitleBy, getHeatmapSymbolTitleByKey);
        props.push(dp);
        dp = new DynProperty('symbolFirstValue', viewSettings.symbolLevelFirstValue, DynProperty.COMBOBOX, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.FirstValue';
        dp.separatorGroup = separatorGroup;
        dp.objectVariants = createContextMenuItemsFromEnum(HeatmapSymbolFirstValue, getHeatmapSymbolFirstValueKey);
        props.push(dp);
        dp = new DynProperty('symbolSecondValue', viewSettings.symbolLevelSecondValue, DynProperty.COMBOBOX, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.SecondValue';
        dp.separatorGroup = separatorGroup;
        dp.objectVariants = createContextMenuItemsFromEnum(HeatmapSymbolSecondValue, getHeatmapSymbolSecondValueKey);
        props.push(dp);
        dp = new DynProperty('symbolForeColor', viewSettings.symbolLevelFontColor, DynProperty.COLOR, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.Color';
        dp.separatorGroup = separatorGroup;
        props.push(dp);
        dp = new DynProperty('isShowSymbolLogo', viewSettings.logoVisible, DynProperty.BOOLEAN, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.Logo';
        dp.separatorGroup = separatorGroup;
        props.push(dp);
        dp = new DynProperty('symbolSizeBy', viewSettings.symbolLevelSizeBy, DynProperty.COMBOBOX, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.SizeBy';
        dp.separatorGroup = separatorGroup;
        dp.objectVariants = createContextMenuItemsFromEnum(HeatmapSymbolSizeBy, getHeatmapSymbolSizeByKey);
        props.push(dp);
        dp = new DynProperty('symbolColorBy', viewSettings.symbolLevelColorBy, DynProperty.COMBOBOX, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.ColorBy';
        dp.separatorGroup = separatorGroup;
        dp.objectVariants = createContextMenuItemsFromEnum(HeatmapSymbolColorBy, getHeatmapSymbolColorByKey);
        props.push(dp);
        separatorGroup = `#3#${Resources.getResource('panel.HeatmapPanel.Settings.GradationScale')}`;
        dp = new DynProperty('isShowGradationScale', viewSettings.gradationScaleVisible, DynProperty.BOOLEAN, DynProperty.VIEW_GROUP);
        dp.localizationKey = 'property.Visible';
        dp.separatorGroup = separatorGroup;
        props.push(dp);
        dp = new DynProperty('selectedListId', this._selectedListId, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP);
        props.push(dp);
        return props;
    }
    callBack(newProperties) {
        super.callBack(newProperties);
        const viewSettings = this._heatmapDrawer.getSettings().viewSettings;
        let dp = DynProperty.getPropertyByName(newProperties, 'sectorBackColor');
        if (!isNullOrUndefined(dp)) {
            viewSettings.sectorLevelBackgroundColor = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'sectorForeColor');
        if (!isNullOrUndefined(dp)) {
            viewSettings.sectorLevelFontColor = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'industryBackColor');
        if (!isNullOrUndefined(dp)) {
            viewSettings.industryLevelBackgroundColor = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'industryForeColor');
        if (!isNullOrUndefined(dp)) {
            viewSettings.industryLevelFontColor = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'symbolScaleColor');
        if (!isNullOrUndefined(dp)) {
            viewSettings.minColor = dp.value.Color1;
            viewSettings.midColor = dp.value.Color2;
            viewSettings.maxColor = dp.value.Color3;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'symbolTitle');
        if (!isNullOrUndefined(dp)) {
            viewSettings.symbolLevelTitleBy = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'symbolFirstValue');
        if (!isNullOrUndefined(dp)) {
            viewSettings.symbolLevelFirstValue = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'symbolSecondValue');
        if (!isNullOrUndefined(dp)) {
            viewSettings.symbolLevelSecondValue = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'symbolForeColor');
        if (!isNullOrUndefined(dp)) {
            viewSettings.symbolLevelFontColor = dp.value;
            void this.set('filterForegroundColor', dp.value);
        }
        dp = DynProperty.getPropertyByName(newProperties, 'isShowSymbolLogo');
        if (!isNullOrUndefined(dp)) {
            viewSettings.logoVisible = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'symbolSizeBy');
        if (!isNullOrUndefined(dp)) {
            viewSettings.symbolLevelSizeBy = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'symbolColorBy');
        if (!isNullOrUndefined(dp)) {
            viewSettings.symbolLevelColorBy = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'isShowGradationScale');
        if (!isNullOrUndefined(dp)) {
            viewSettings.gradationScaleVisible = dp.value;
        }
        dp = DynProperty.getPropertyByName(newProperties, 'selectedListId');
        if (!isNullOrUndefined(dp)) {
            this._selectedListId = dp.value;
        }
        this._heatmapDrawer.applySettings();
        void this.set('isShowHeatmapFilter', viewSettings.gradationScaleVisible);
        this.populateIntervals();
    }
    symbolLink_Out(newSubscriber, instrument) {
        if (isNullOrUndefined(instrument)) {
            return;
        }
        const color = super.get('symbolLinkValue');
        if (color !== TerceraLinkControlConstants.STATE_NONE) {
            LinkedSystem.setSymbol(color, instrument.GetInteriorID(), newSubscriber);
        }
    }
    closeAddidionalScreens(mainComponent) {
        const controls = mainComponent.Controls;
        for (const control of Object.values(controls)) {
            if (control instanceof PropertySetupScreen ||
                control instanceof AdvancedOrderEntry ||
                control instanceof SymbolInfoPanel) {
                control.close();
            }
        }
    }
    showContextMenu(event) {
        const hoveredInstrument = this._heatmapDrawer.getHoveredInstrument();
        const contextMenuItems = [];
        if (!isNullOrUndefined(hoveredInstrument)) {
            if (CustomerAccess.panelAllowed(PanelNames.SymbolInfoPanel)) {
                contextMenuItems.push({
                    type: NewContextMenuItemType.Default,
                    text: 'panel.watchlist.menu.SymbolInfo',
                    svgImage: 'Info window',
                    event: () => MainWindowManager.MainWindow.ShowSymbolInfo(hoveredInstrument)
                });
            }
            contextMenuItems.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.neworder',
                svgImage: 'orderEntry',
                event: () => {
                    const exitFs = document.fullscreenElement
                        ? document.exitFullscreen()
                        : Promise.resolve();
                    exitFs.then(() => {
                        MainWindowManager.MainWindow.ShowOrderEntry(hoveredInstrument);
                    });
                }
            });
            if (CustomerAccess.panelAllowed(PanelNames.ChartPanel)) {
                contextMenuItems.push({
                    type: NewContextMenuItemType.Default,
                    text: 'panel.chart',
                    svgImage: 'chartFromHeatmap',
                    event: () => MainWindowManager.MainWindow.ShowChart(hoveredInstrument)
                });
            }
            contextMenuItems.push({ type: NewContextMenuItemType.Separator });
            if (CustomerAccess.panelAllowed(PanelNames.InformerPanel)) {
                const addToWatchlistItem = {
                    type: NewContextMenuItemType.Default,
                    text: 'general.addToWatchlist',
                    svgImage: 'watchListIcon',
                    subitems: []
                };
                const wlItems = WatchlistManager.getItems();
                wlItems.forEach((item) => {
                    addToWatchlistItem.subitems.push({
                        type: NewContextMenuItemType.Default,
                        text: item.name,
                        event: () => {
                            if (!isNullOrUndefined(hoveredInstrument)) {
                                WatchlistManager.addSymbolIDToItemByName(item.name, hoveredInstrument.GetInteriorID());
                            }
                        }
                    });
                });
                contextMenuItems.push(addToWatchlistItem);
                contextMenuItems.push({ type: NewContextMenuItemType.Separator });
            }
        }
        contextMenuItems.push({
            type: NewContextMenuItemType.Default,
            text: 'general.Settings',
            svgImage: 'settings',
            event: () => {
                PanelSettingsScreen.EditProperties(this, null, Resources.getResource('panel.HeatmapPanel.Settings.Header'));
            }
        });
        showContextMenu({ x: event.original.clientX, y: event.original.clientY, items: contextMenuItems });
    }
    populateListItems() {
        const heatmapCustomLists = DataCache.GetCustomListsByType(CustomInstrumentListTypeEnum.MarketHeatMap);
        void this.set('heatmapListItems', heatmapCustomLists);
        if (heatmapCustomLists.length > 0) {
            let selectedList = heatmapCustomLists[0];
            if (isValidNumber(this._selectedListId)) {
                for (let i = 0; i < heatmapCustomLists.length; i++) {
                    if (heatmapCustomLists[i].id === this._selectedListId) {
                        selectedList = heatmapCustomLists[i];
                        break;
                    }
                }
            }
            void this.set('selectedListItem', selectedList);
        }
        else {
            void this.set('selectedListItem', null);
        }
        this.updateListItemsVisiblity();
    }
    populateIntervals() {
        this._heatmapDrawer.populateIntervals();
        void this.set('intervals', this._heatmapDrawer.getIntervals());
        void this.set('scaleMultiplier', this._heatmapDrawer.getStepMultiplier());
        void this.updateModel();
    }
    listItemClicked(_event, id) {
        const heatmapListItems = this.get('heatmapListItems');
        for (const item of heatmapListItems) {
            if (item.id === id) {
                this._selectedListId = id;
                void this.set('selectedListItem', item);
                if (isNullOrUndefined(_event)) {
                    this.updateListItemsVisiblity();
                }
                break;
            }
        }
    }
    onScaleFilterChanged() {
        this._heatmapDrawer.applySettings();
    }
    onScaleMultiplierChanged(_sender, scaleMultiplier) {
        if (isNullOrUndefined(this._heatmapDrawer)) {
            return;
        }
        this._heatmapDrawer.setStepMultiplier(scaleMultiplier);
        this.populateIntervals();
    }
    onMoreButtonClick(_sender) {
        const heatmapContextMenuListItems = this.get('heatmapContextMenuListItems');
        const selectedListItem = this.get('selectedListItem');
        const contextMenuItems = [];
        for (let i = 0; i < heatmapContextMenuListItems.length; i++) {
            contextMenuItems.push({
                type: NewContextMenuItemType.Default,
                text: heatmapContextMenuListItems[i].name,
                canCheck: true,
                checked: heatmapContextMenuListItems[i] === selectedListItem,
                event: () => {
                    this.listItemClicked(undefined, heatmapContextMenuListItems[i].id);
                }
            });
        }
        const rect = this.findAll('.terceraButton.js-more-button')[0].getBoundingClientRect();
        showContextMenu({ x: rect.left, y: rect.bottom, items: contextMenuItems });
    }
    selectedListItemChanged(newValue, oldValue) {
        if (isNullOrUndefined(this._heatmapDrawer)) {
            return;
        }
        if (!isNullOrUndefined(oldValue)) {
            DataCache.HeatmapCache.processSubscription(oldValue.id, false, this.onUpdateItems);
        }
        this._marketCapCache.clear();
        const selectedListItem = this.get('selectedListItem');
        const data = [];
        if (!isNullOrUndefined(selectedListItem) && !isNullOrUndefined(selectedListItem.tradableIds)) {
            const ids = selectedListItem.tradableIds;
            for (const tradableId of ids) {
                const instr = DataCache.getInstrumentByTradable_ID_first(tradableId);
                if (!isNullOrUndefined(instr) && instr.MarketCap !== 0) {
                    this._marketCapCache.set(instr, instr.MarketCap);
                    const heatmapData = new HeatmapData(instr);
                    data.push(heatmapData);
                }
            }
        }
        this._heatmapDrawer.populate(data);
        if (!isNullOrUndefined(newValue)) {
            DataCache.HeatmapCache.processSubscription(newValue.id, true, this.onUpdateItems);
        }
    }
    onUpdateInstrument(instrument) {
        if (isNullOrUndefined(instrument)) {
            return;
        }
        const oldMarketCap = this._marketCapCache.get(instrument);
        if (!isValidNumber(oldMarketCap)) {
            return;
        }
        if (oldMarketCap !== instrument.MarketCap) {
            this._marketCapCache.set(instrument, instrument.MarketCap);
            this._heatmapDrawer.updateMap();
        }
    }
    updateListItemsVisiblity() {
        const listItems = this.get('heatmapListItems');
        const selectedListItem = this.get('selectedListItem');
        if (isNullOrUndefined(listItems) || isNullOrUndefined(selectedListItem)) {
            void this.set({
                heatmapHeaderListItems: [],
                heatmapContextMenuListItems: []
            });
            return;
        }
        let startHeaderIndex = 0;
        let endHeaderIndex = listItems.length - 1;
        const selectedIndex = listItems.indexOf(selectedListItem);
        const nextAfterSelectedItemsCount = listItems.length - selectedIndex - 1;
        if (selectedIndex <= HeatmapPanel.HEADER_LIST_ITEMS_COUNT - 1) {
            endHeaderIndex = HeatmapPanel.HEADER_LIST_ITEMS_COUNT - 1;
        }
        else if (nextAfterSelectedItemsCount >= HeatmapPanel.HEADER_LIST_ITEMS_COUNT) {
            startHeaderIndex = selectedIndex;
            endHeaderIndex = selectedIndex + HeatmapPanel.HEADER_LIST_ITEMS_COUNT - 1;
        }
        else {
            startHeaderIndex = listItems.length - HeatmapPanel.HEADER_LIST_ITEMS_COUNT;
            endHeaderIndex = listItems.length - 1;
        }
        const headerListItems = [];
        const contextMenuListItems = [];
        for (let i = 0; i < listItems.length; i++) {
            const listItem = listItems[i];
            if (i >= startHeaderIndex && i <= endHeaderIndex) {
                headerListItems.push(listItem);
            }
            else {
                contextMenuListItems.push(listItem);
            }
        }
        void this.set({
            heatmapHeaderListItems: headerListItems,
            heatmapContextMenuListItems: contextMenuListItems
        });
    }
}
HeatmapPanel.HEADER_LIST_ITEMS_COUNT = 5;
ApplicationPanel.extendWith(HeatmapPanel, {
    data: function () {
        return {
            showFullscreenModeButton: true,
            canLinkByAccount: false,
            isSymbolLinkShow: true,
            heatmapListItems: [],
            heatmapHeaderListItems: [],
            heatmapContextMenuListItems: [],
            selectedListItem: null,
            intervals: [],
            isShowHeatmapFilter: true,
            scaleMultiplier: undefined,
            hoveredHeatmapData: null,
            filterForegroundColor: ''
        };
    },
    partials: {
        bodyPartial: HeatmapPanelTemplate
    }
});
